import * as React from 'react';
import { styled } from '@linaria/react';

import { normalize, range } from '@/utils';
import {
  VIEWPORT_HEIGHT,
  BOX_SIZE,
} from './ScrollViewProgress.constants';

const YELLOW = 'hsl(45deg 100% 50%)';

type Edge = 'start' | 'end' | 'middle';

function getTickHighlightAmount(
  progressPct: number,
  tickValue: number,
  edge: Edge,
  highlightFalloff: number
): number {
  const rangeStart = Math.max(0, tickValue - highlightFalloff);
  const rangeEnd = Math.min(100, tickValue + highlightFalloff);

  if (progressPct < rangeStart || progressPct > rangeEnd) {
    return 0;
  }

  const t = normalize(progressPct, rangeStart, rangeEnd, 0, 1);

  if (edge === 'start') {
    return 1 - t;
  }
  if (edge === 'end') {
    return t;
  }
  if (t < 0.4) {
    return t / 0.4;
  }
  if (t > 0.6) {
    return (1 - t) / 0.4;
  }
  return 1;
}

interface ScaleProps {
  animationRange?: 'cover' | 'contain' | 'entry' | 'exit' | 'mixed';
  progress: number | null;
  isAnimationTimelineSupported: boolean;
  isProgressVisible: boolean;
}

function Scale({
  animationRange = 'cover',
  progress,
  isAnimationTimelineSupported,
  isProgressVisible,
}: ScaleProps) {
  const highlightFalloff = animationRange === 'entry' ? 10 : 5;
  const isCompact =
    animationRange === 'entry' ||
    animationRange === 'exit' ||
    animationRange === 'mixed';
  const isMixed = animationRange === 'mixed';
  const tickStep = isCompact ? 10 : 5;
  const tickValues = range(0, 101, tickStep).reverse();
  const majorTickInterval = isCompact ? 50 : 20;

  return (
    <Wrapper
      aria-hidden
      data-animation-range={animationRange}
      data-is-progress-visible={isProgressVisible}
      style={{
        // `cover` keeps `both` so boundary ticks can persist while active.
        '--animation-fill-mode':
          animationRange === 'cover' ? 'both' : 'none',
      }}
    >
      {tickValues.map((value) => {
        let rangeStart: number;
        let rangeEnd: number;
        if (isMixed) {
          const coverCenter = value / 2;
          rangeStart = Math.max(0, coverCenter - highlightFalloff);
          rangeEnd = Math.min(50, coverCenter + highlightFalloff);
        } else {
          rangeStart = Math.max(0, value - highlightFalloff);
          rangeEnd = Math.min(100, value + highlightFalloff);
        }
        const edge: Edge =
          value === 0 ? 'start' : value === 100 ? 'end' : 'middle';

        let tickStyle: React.CSSProperties | undefined;
        let labelStyle: React.CSSProperties | undefined;

        if (!isAnimationTimelineSupported) {
          const progressPct = progress !== null ? progress * 100 : 0;
          const highlightAmount =
            progress !== null
              ? getTickHighlightAmount(
                  progressPct,
                  value,
                  edge,
                  highlightFalloff
                )
              : 0;
          const isHighlighted = highlightAmount > 0.5;

          tickStyle = {
            background: isHighlighted ? YELLOW : undefined,
          };
          labelStyle = {
            color: isHighlighted ? YELLOW : undefined,
            fontWeight: isHighlighted ? 'bold' : undefined,
          };
        }

        return (
          <TickRow
            key={value}
            style={{
              '--tick-position': isMixed
                ? `${100 - value / 2}%`
                : `${100 - value}%`,
              '--range-start': `${rangeStart}%`,
              '--range-end': `${rangeEnd}%`,
            }}
            data-is-major={value % majorTickInterval === 0}
            data-edge={edge}
          >
            {value % majorTickInterval === 0 && (
              <TickLabel style={labelStyle}>{value}%</TickLabel>
            )}
            <Tick style={tickStyle} />
          </TickRow>
        );
      })}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  margin-block: 1px;
  width: 64px;
  height: ${VIEWPORT_HEIGHT - 2}px;
  flex-shrink: 0;

  &[data-animation-range='entry'] {
    height: ${BOX_SIZE - 2}px;
    align-self: flex-end;
    margin-block: 0;
    margin-bottom: 1px;
  }

  &[data-animation-range='exit'] {
    height: ${BOX_SIZE - 2}px;
    align-self: flex-start;
    margin-block: 0;
    margin-top: 1px;
  }
`;

const TickRow = styled.div`
  --tick-position: 0%;
  --range-start: 0%;
  --range-end: 100%;
  position: absolute;
  left: 0;
  top: var(--tick-position);
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  transform: translateY(-50%);
`;

const Tick = styled.div`
  --tick-idle-color: var(--color-gray-300);
  width: 8px;
  height: 2px;
  background: var(--tick-idle-color);
  border-radius: 100px;

  ${TickRow}[data-is-major='true'] & {
    --tick-idle-color: var(--color-gray-700);
    width: 14px;
  }

  @supports (animation-timeline: scroll()) {
    ${Wrapper}[data-is-progress-visible='true'] & {
      animation: tickHighlight linear var(--animation-fill-mode);
      animation-timeline: --content;
      animation-range: var(--tick-timeline-range-name)
        var(--range-start) var(--tick-timeline-range-name)
        var(--range-end);
    }

    ${TickRow}[data-edge='start'] & {
      animation-name: tickHighlightStart;
    }

    ${TickRow}[data-edge='end'] & {
      animation-name: tickHighlightEnd;
    }

    ${TickRow}[data-edge='middle'] & {
      animation-name: tickHighlightMiddle;
    }
  }

  @keyframes tickHighlightMiddle {
    from {
      background: var(--tick-idle-color);
    }
    40% {
      background: var(--yellow);
    }
    60% {
      background: var(--yellow);
    }
    to {
      background: var(--tick-idle-color);
    }
  }

  @keyframes tickHighlightStart {
    from {
      background: var(--yellow);
    }
    to {
      background: var(--tick-idle-color);
    }
  }

  @keyframes tickHighlightEnd {
    from {
      background: var(--tick-idle-color);
    }
    to {
      background: var(--yellow);
    }
  }
`;

const TickLabel = styled.span`
  width: 36px;
  font-size: 0.75rem;
  text-align: right;
  font-weight: normal;
  font-variant-numeric: tabular-nums;
  color: var(--color-gray-700);

  @supports (animation-timeline: scroll()) {
    ${Wrapper}[data-is-progress-visible='true'] & {
      animation: tickLabelHighlight linear var(--animation-fill-mode);
      animation-timeline: --content;
      animation-range: var(--tick-timeline-range-name)
        var(--range-start) var(--tick-timeline-range-name)
        var(--range-end);
    }

    ${TickRow}[data-edge='start'] & {
      animation-name: tickLabelHighlightStart;
    }

    ${TickRow}[data-edge='end'] & {
      animation-name: tickLabelHighlightEnd;
    }

    ${TickRow}[data-edge='middle'] & {
      animation-name: tickLabelHighlightMiddle;
    }
  }

  @keyframes tickLabelHighlightMiddle {
    from {
      color: var(--color-gray-700);
    }
    40% {
      color: var(--yellow);
      font-weight: bold;
    }
    60% {
      color: var(--yellow);
      font-weight: bold;
    }
    to {
      color: var(--color-gray-700);
    }
  }

  @keyframes tickLabelHighlightStart {
    from {
      color: var(--yellow);
      font-weight: bold;
    }
    to {
      color: var(--color-gray-700);
    }
  }

  @keyframes tickLabelHighlightEnd {
    from {
      color: var(--color-gray-700);
    }
    to {
      color: var(--yellow);
      font-weight: bold;
    }
  }
`;

export default Scale;
